/** @jsxRuntime classic */
/** @jsx jsx */
import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { css, jsx } from '@emotion/react';
import { useFolderOrganizationQuery } from '../../actions/FolderActions';
import { useUserQuery } from '../../actions/UserActions';
import Text from '@mui/material/Typography';
import Button from '@mui/material/Button';

import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import DriveFolderUploadIcon from '@mui/icons-material/DriveFolderUpload';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import AssetDialog from '../folders/assets/AssetDialog';

function noop() {}

export default function FolderLocation({
  currentFolderPrefix = '',
  onChange = noop,
  allowRoot = false,
  workspaceId,
  disabled = false,
  asButton = false,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const { user } = useUserQuery();

  const { folders: _folders, currentFolder } = useFolderOrganizationQuery({
    organizationId: workspaceId || user?.workspace?.id,
    folderPrefix: currentFolderPrefix,
    onlyEditAccess: true,
  });

  const folders = _folders?.filter((x) => !x.user_fallback);

  const isRootFolder =
    currentFolderPrefix.split('/').filter((x) => x).length === 1;
  const error = !currentFolderPrefix;

  const folderPath = useMemo(() => {
    return currentFolder?.parents
      ?.slice(1)
      ?.map((x) => x.name)
      .join('/');
  }, [currentFolder]);

  const handleOpen = useCallback(
    (event) => {
      if (disabled) return;
      setAnchorEl(event.currentTarget);
      setOpen(true);
    },
    [disabled]
  );

  const close = useCallback((assets) => {
    const asset = Object.values(assets || {})?.[0];
    if (asset?.type === 'folder') {
      onChange(asset.folderPrefix);
    }

    setOpen(false);
  }, []);

  const handleBack = useCallback(() => {
    const parts = currentFolderPrefix.split('/');
    parts.pop();
    onChange(parts.join('/'));
  }, [currentFolderPrefix, onChange]);

  return (
    <div>
      {asButton ? (
        <div
          css={css`
            display: flex;
            flex-direction: column;
          `}
        >
          <Button
            onClick={handleOpen}
            disabled={disabled}
            startIcon={<DriveFolderUploadIcon />}
            endIcon={<ArrowDropDownIcon />}
            color={
              (!allowRoot && isRootFolder) || error
                ? 'danger'
                : open
                ? 'primary'
                : 'text'
            }
          >
            {(!allowRoot && isRootFolder) || error
              ? t('Välj vilken mapp')
              : folderPath?.length
              ? `${folderPath}/`
              : t('Välj mapp')}
          </Button>
        </div>
      ) : (
        <>
          <Text
            variant="body1"
            css={css`
              width: 100%;
              border: 1px solid
                ${(!allowRoot && isRootFolder) || error
                  ? 'var(--error-main)'
                  : '#e0e0e0'};
              border-radius: 8px;
              padding: 0.5rem 1rem;
              box-sizing: border-box;
              cursor: ${disabled ? 'auto' : 'pointer'};
            `}
            color={folderPath?.length ? 'text.primary' : 'text.secondary'}
            onClick={handleOpen}
          >
            {folderPath?.length ? `${folderPath}/` : t('Välj mapp')}
          </Text>
          {((!allowRoot && isRootFolder) || error) && (
            <Text color="error.main" variant="caption">
              {t('Välj vilken mapp')}
            </Text>
          )}
        </>
      )}

      <AssetDialog
        open={open}
        onClose={close}
        data={{
          folderPrefix: currentFolderPrefix,
          types: ['folder'],
        }}
        singleSelect
        hideFilters
      />
    </div>
  );
}
